import React, { FC, useCallback, useRef, useState } from 'react';

import { MdAdd, MdOutlineApartment, MdOutlineLocationOn } from 'react-icons/md';

import {
  Box,
  Flex,
  Icon,
  Image,
  Link,
  Stack,
  Text,
  Button,
  Divider,
  SimpleGrid,
  Spinner,
} from '@chakra-ui/react';

import { Form, Formik } from 'formik';
import { useQuery } from 'react-query';

import rCup from 'assets/logos/r.World-company-logo.png';
import cmsBg from 'assets/rworld-banner.png';

import api from 'api/instance';

import FormikInput from 'components/primitives/FormikInput';
import FormikTextArea from 'components/primitives/FormikTextArea';

import { getInitialValues } from './SitePortal.utils';

import { SitePortalViewProps } from './SitePortal.props';

function alphanumericSort(a: any, b: any) {
  return String(a?.name)
    .toLowerCase()
    .localeCompare(String(b?.name).toLowerCase());
}

function handleSort(packages: any[]) {
  const sortedPackages = [...packages].sort(alphanumericSort);

  return sortedPackages;
}

const URL = `${process.env.REACT_APP_API_URL}`;

const formatCount = (value: number): string => {
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    const formatted =
      millions % 1 === 0
        ? millions.toFixed(0)
        : millions.toFixed(1).replace(/\.0$/, '');
    return `${formatted}+ Million`;
  }
  if (value >= 1_000) {
    const thousands = value / 1_000;
    const formatted =
      thousands % 1 === 0
        ? thousands.toFixed(0)
        : thousands.toFixed(1).replace(/\.0$/, '');
    return `${formatted}K+`;
  }
  return value.toLocaleString();
};

const SitePortalView: FC<SitePortalViewProps> = ({
  data,
  onSubmit,
  isCreating,
  onLogoSelect,
  isUploadingLogo,
}) => {
  const packages = handleSort(data?.packages || []);

  const siteAddress =
    (data as any)?.site?.address?.fullAddress ||
    (data as any)?.site?.address?.addressLine1 ||
    '';

  // Set when the customer's logo fails to load (expired presigned URL, missing
  // S3 object) so we fall through to the same neutral placeholder.
  const [hasLogoError, setHasLogoError] = useState(false);

  const logoInputRef = useRef<HTMLInputElement>(null);

  const onLogoPick = useCallback(() => {
    if (isUploadingLogo) {
      return;
    }

    logoInputRef?.current?.click();
  }, [isUploadingLogo]);

  const onLogoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];

      if (file) {
        // A previous failure shouldn't keep the placeholder pinned once a new
        // image arrives.
        setHasLogoError(false);
        onLogoSelect(file);
      }

      // Clear the value so re-picking the same file still fires onChange.
      e.target.value = '';
    },
    [onLogoSelect],
  );

  const { data: statsData } = useQuery(['fetch-items-eliminated'], async () => {
    const { data } = await api.get(`${URL}/system-stats/items-eliminated`);
    return data?.item as { value: number; lastUpdated: string };
  });

  return (
    <Flex flex={1} direction="column">
      <Flex px="10" py="5" shadow="lg" align="center" justify="space-between">
        <Image src={rCup} maxW="182px" cursor="pointer" />
      </Flex>
      <Flex direction="column">
        <Box
          position="relative"
          w="100%"
          sx={{
            aspectRatio: '1162 / 200',
            containerType: 'inline-size',
          }}>
          <Image
            src={cmsBg}
            w="100%"
            h="100%"
            objectFit="fill"
            display="block"
          />
          {statsData?.value ? (
            <Text
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              fontSize="7.75cqw"
              fontWeight="500"
              fontFamily="'Poppins', sans-serif"
              color="#4D4D4D"
              opacity={0.85}
              lineHeight="1"
              textAlign="center"
              whiteSpace="nowrap">
              {formatCount(statsData.value)}
            </Text>
          ) : null}
        </Box>
        {/* The banner's height scales with its width, so below lg it is too
            short for the logo to overlap it without covering the banner text;
            there the header stacks under the banner instead. */}
        <Flex
          position="relative"
          zIndex={1}
          mt={{ base: 4, lg: '-80px' }}
          bg="transparent"
          align={{ base: 'center', lg: 'flex-end' }}
          alignSelf={{ base: 'stretch', lg: 'flex-start' }}
          direction={{ base: 'column', lg: 'row' }}>
          {/* Customer logo, and the control for setting one. When the customer
              has none (or theirs fails to load) we show a neutral building
              placeholder rather than an r.World mark — this badge sits beside
              the venue's own name, so r.World branding here reads as if the
              venue were r.World. overflow stays visible so the add button can
              overhang the corner; the image itself is clipped by its own box. */}
          <Box
            as="button"
            type="button"
            pos="relative"
            mb={{ base: 6, lg: 0 }}
            h="160px"
            w="160px"
            flexShrink={0}
            ml={{ base: 0, lg: '100px' }}
            aria-label={data?.logo ? 'Replace logo' : 'Add logo'}
            cursor={isUploadingLogo ? 'wait' : 'pointer'}
            onClick={onLogoPick}>
            <input
              type="file"
              ref={logoInputRef}
              accept="image/jpg, image/jpeg, image/png"
              style={{ display: 'none' }}
              onChange={onLogoChange}
            />

            <Flex
              h="100%"
              w="100%"
              bg="#fff"
              align="center"
              justify="center"
              borderRadius="20px"
              overflow="hidden"
              boxShadow="0 0 20px 0 rgba(0,0,0,0.2)">
              {data?.logo && !hasLogoError ? (
                <Image
                  h="160px"
                  w="160px"
                  borderRadius="20px"
                  src={data.logo}
                  objectFit="contain"
                  onError={() => setHasLogoError(true)}
                />
              ) : (
                <Icon
                  as={MdOutlineApartment}
                  boxSize="100px"
                  color="support.400"
                />
              )}
            </Flex>

            {/* Layered over the bottom-right corner so it reads as a button on
                the image rather than a caption beside it. */}
            <Flex
              pos="absolute"
              right="-6px"
              bottom="-6px"
              boxSize="44px"
              bg="primary.500"
              color="#fff"
              align="center"
              justify="center"
              borderRadius="full"
              border="3px solid #fff"
              boxShadow="0 2px 6px rgba(0,0,0,0.25)">
              <Icon as={MdAdd} boxSize="24px" />
            </Flex>

            {isUploadingLogo && (
              <Flex
                pos="absolute"
                inset={0}
                align="center"
                justify="center"
                bg="rgba(255,255,255,0.75)"
                borderRadius="20px">
                <Spinner size="md" color="primary.500" />
              </Flex>
            )}
          </Box>
          {/* minW 0 lets a long address wrap inside the remaining width
              instead of pushing the header past the screen edge. */}
          <Stack
            mb={2}
            spacing={0}
            minW={0}
            flex={{ lg: 1 }}
            ml={{ base: 0, lg: 10 }}
            pl={{ base: 4, lg: 0 }}
            pr={{ base: 4, lg: 10 }}
            alignSelf={{ base: 'stretch', lg: 'flex-end' }}>
            <Text
              textAlign={{ base: 'center', lg: 'left' }}
              fontWeight="bold"
              fontSize="26px">
              {(data as any)?.site?.name || ''}
            </Text>
            {siteAddress ? (
              <Flex
                gap={1.5}
                align="flex-start"
                justify={{ base: 'center', lg: 'flex-start' }}>
                <Icon
                  as={MdOutlineLocationOn}
                  boxSize={{ base: '16px', lg: '18px' }}
                  mt={{ base: '2px', lg: '3px' }}
                  flexShrink={0}
                  color="gray.500"
                  aria-hidden
                />
                <Text
                  minW={0}
                  fontSize={{ base: '14px', lg: '16px' }}
                  overflowWrap="anywhere">
                  {siteAddress}
                </Text>
              </Flex>
            ) : null}
          </Stack>
        </Flex>
        <Divider mt="50px" />
      </Flex>

      <Formik
        enableReinitialize
        onSubmit={(values, helpers) => {
          onSubmit(values);
          helpers.resetForm();
        }}
        initialValues={getInitialValues(packages)}>
        {({ values }) => (
          <Form>
            <Flex p="20px" flex={1} justify="center">
              <Flex w="100%" maxW="1200px" direction="column">
                <Text textAlign="center" fontSize="4xl" fontWeight="bold">
                  New Order
                </Text>
                <SimpleGrid
                  mb="20px"
                  flexWrap="wrap"
                  columns={{ base: 1, md: 2, lg: 3 }}
                  spacing={4}>
                  {packages?.map((p, index) => (
                    <FormikInput
                      key={JSON.stringify(p)}
                      name={`details[${index}].casesCount`}
                      label={`Cases of ${p?.name || ''}`}
                      helperText={p?.description || ''}
                      type="number"
                      labelProps={{ fontSize: '14px', color: '#4f4f4f' }}
                      pattern="[0-9]*"
                      inputMode="numeric"
                    />
                  ))}
                </SimpleGrid>
                <FormikTextArea
                  name="notes"
                  label="Notes, PO#'s, Delivery Date Needed By, Questions, Feedback, etc."
                  minH="120px"
                  value={values?.notes}
                  labelProps={{ fontSize: '14px', color: '#4f4f4f' }}
                />
                <Button
                  mb="100px"
                  isLoading={isCreating}
                  type="submit"
                  mt="50px"
                  size="lg"
                  alignSelf="center"
                  w="200px">
                  Submit Form
                </Button>
              </Flex>
            </Flex>
          </Form>
        )}
      </Formik>
    </Flex>
  );
};

export default SitePortalView;
